import React from 'react';
import { CheckCircle } from 'lucide-react';
import { DialogShell } from '../ui/DialogShell';

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const formatMonto = (monto: number) =>
  monto.toLocaleString('es-CL', { style: 'currency', currency: 'CLP' });

interface CuotaIglesiaConfirmDialogProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly tipo: 'jad' | 'jan';
  readonly entidadNombre: string;
  readonly mes: number;
  readonly anio: number;
  readonly montoAJAL?: number;
  readonly montoPA?: number;
  readonly montoRegular?: number;
  readonly nLumisiales?: number;
  readonly montoPAConfirmados?: number;
}

export const CuotaIglesiaConfirmDialog: React.FC<CuotaIglesiaConfirmDialogProps> = ({
  isOpen,
  onClose,
  tipo,
  entidadNombre,
  mes,
  anio,
  montoAJAL = 0,
  montoPA = 0,
  montoRegular = 0,
  nLumisiales,
  montoPAConfirmados = 0
}) => {
  const periodoLabel = `${MESES[mes - 1]} ${anio}`;

  const renderResumen = (total: number, filas: { label: string; monto: number }[]) => (
    <div className="space-y-3">
      <div className="text-sm text-slate-500">{entidadNombre} · {periodoLabel}</div>
      <div className="bg-slate-50 rounded-lg px-4 py-3 space-y-2">
        <div className="flex justify-between items-center">
          <span className="text-sm font-semibold text-slate-700">Total</span>
          <span className="text-base font-bold text-emerald-700">{formatMonto(total)}</span>
        </div>
        {filas.length > 1 && (
          <div className="border-t border-slate-200 pt-2 space-y-1.5">
            {filas.map(({ label, monto }) => (
              <div key={label} className="flex justify-between items-center text-sm text-slate-600">
                <span>{label}</span>
                <span className="font-medium">{formatMonto(monto)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderBodyJAD = () => {
    const tieneAJAL = montoAJAL > 0;
    const tienePA = montoPA > 0;
    const total = montoAJAL + montoPA;

    const filas: { label: string; monto: number }[] = [];
    if (tieneAJAL) filas.push({ label: 'Transferencia JAL', monto: montoAJAL });
    if (tienePA) filas.push({ label: 'Pago automático', monto: montoPA });

    return renderResumen(total, filas);
  };

  const renderBodyJAN = () => {
    const total = montoRegular + montoPAConfirmados;

    const filas: { label: string; monto: number }[] = [];
    if (montoRegular > 0) {
      const labelLumisial = nLumisiales === 1 ? 'lumisial' : 'lumisiales';
      const sufijo = nLumisiales !== undefined && nLumisiales > 0
        ? ` (${nLumisiales} ${labelLumisial})`
        : '';
      filas.push({ label: `Transferencia JAD${sufijo}`, monto: montoRegular });
    }
    if (montoPAConfirmados > 0) filas.push({ label: 'Pago automático', monto: montoPAConfirmados });

    return renderResumen(total, filas);
  };

  return (
    <DialogShell
      isOpen={isOpen}
      onClose={onClose}
      title="Cuota iglesia registrada"
      size="sm"
      icon={<CheckCircle className="w-6 h-6 text-emerald-500" />}
    >
      <div className="p-6 space-y-6">
        {tipo === 'jad' ? renderBodyJAD() : renderBodyJAN()}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors"
          >
            Entendido
          </button>
        </div>
      </div>
    </DialogShell>
  );
};
